import { DataGrid } from '@mui/x-data-grid';
import { groupBy } from 'lodash';
import { v7 as uuidv7 } from 'uuid';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

// Commons
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { Dropdown, INPUT_WIDTH } from '@commons/utils/styledLibraryComponents';
import { formatNumber } from '@commons/DisplayNumber';
import { generateUUID } from '@commons/utils/uuid';
import { REGEX_IMAGE_FILE_EXTENSION } from '@commons/utils/regex';
import Text, { ENUM_FONTS } from '@commons/Text';

// Style & components
import {
  DataGridContainer,
  DataGridHeader,
  DataGridHeaderTextContainer,
} from '../styledComponents';
import { GridContainer } from './styledComponents';
import EmptyState from '@stocks/StocksInventories/StocksInventoriesTransfer/components/TransferForm/components/EmptyState';

// Miscellaneous
import { getSupplierProductsColumns } from './getColumns';
import utils from '@admin/components/HeaderPicture/utils/files';

const renderEmptyState = (selectedDate, selectedStore) => (
  <>
    <EmptyState
      icon={'/images/inpulse/product.svg'}
      subtitle={i18next.t('LOSSES.PRODUCTS.NO_LOSSES_SUBTITLE', {
        date: selectedDate.format(DATE_DISPLAY_FORMATS.DATE_MONTH),
        storeName: selectedStore.name,
      })}
      title={i18next.t('LOSSES.PRODUCTS.NO_LOSSES_TITLE')}
    />
  </>
);

const SupplierProductsGrid = (props) => {
  const {
    lossCategories,
    currency,
    supplierProductRows,
    setSupplierProductRows,
    isLoading,
    supplierProductsTotal,
    setSupplierProductsTotal,
    supplierProductsDropdownItems,
    setSupplierProductsDropdownItems,
    setIsDirty,
    canCreateOrUpdateLosses,
    canCorrectLosses,
    selectedDate,
    selectedStore,
    setShouldPictureFullScreen,
  } = props;

  const [columns, setColumns] = useState([]);

  /**
   * Updates a supplier product loss with packaging and category assignments ensuring uniqueness
   *
   * @param {Object} loss - The loss object to be updated with packaging and category
   * @param {Array<string>} allAssociatedLossesCompositeKeys - Array of existing composite keys in format "packagingId-lossCategoryId"
   * @returns {Object} The updated loss with assigned packagingId, lossCategoryId and composite key
   *
   * The function:
   * 1. Finds the first available packaging that hasn't used all its loss categories
   * 2. Assigns the first available loss category that isn't used for the selected packaging
   * 3. Creates and assigns a composite key using packagingId-lossCategoryId format
   */
  const updateLossWithCategoryUnicity = (loss, allAssociatedLossesCompositeKeys) => {
    const updatedLoss = { ...loss };

    updatedLoss.packagings.forEach((packaging) => {
      if (updatedLoss.supplierProductPackagingId) {
        return;
      }

      const compositeKeysForPackaging = allAssociatedLossesCompositeKeys.filter((key) =>
        key.includes(packaging.id),
      );

      if (compositeKeysForPackaging.length === lossCategories.length) {
        return;
      }

      updatedLoss.supplierProductPackagingId = packaging.id;
    });

    lossCategories.forEach((lossCategory) => {
      if (updatedLoss.lossCategoryId !== undefined) {
        return;
      }

      if (
        allAssociatedLossesCompositeKeys.includes(
          `${updatedLoss.supplierProductPackagingId}-${lossCategory.id}`,
        )
      ) {
        return;
      }

      updatedLoss.lossCategoryId = lossCategory.id;
    });

    updatedLoss.key = `${updatedLoss.supplierProductPackagingId}-${updatedLoss.lossCategoryId}`;

    return updatedLoss;
  };

  /**
   * Handles adding a new supplier product loss to the grid
   *
   * @param {Object} loss - The supplier product loss to be added
   *
   * The function:
   * 1. Creates a formatted loss object with required fields
   * 2. Gets all existing losses for this supplier product
   * 3. Updates the loss with a valid packaging and category combination
   * 4. Adds the new loss to the grid
   * 5. Updates the dropdown items to disable products that have used all possible combinations
   */
  const addNewLoss = (loss) => {
    let formattedLoss = {
      ...loss,
      lossPictureUrl: null,
      lossPictureImage: null,
      lossCategoryId: undefined, // Undefined instead of null to keep the difference between no category and category none
      supplierProductPackagingId: null,
      id: generateUUID(),
    };

    setIsDirty(true);
    const supplierProductRowGroupedBySPids = groupBy(supplierProductRows, 'supplierProductId');
    const associatedLosses = supplierProductRowGroupedBySPids[loss.supplierProductId] || [];

    const allAssociatedLossesCompositeKeys = associatedLosses.reduce((acc, { key }) => {
      if (!!key) {
        acc.push(key);
      }

      return acc;
    }, []);

    const updatedLoss = updateLossWithCategoryUnicity(
      formattedLoss,
      allAssociatedLossesCompositeKeys,
    );

    setSupplierProductRows([updatedLoss, ...supplierProductRows]);

    const allPossibleKeysForSP = loss.packagings.reduce((acc, packaging) => {
      // Check that the packaging exists and has an id
      if (!packaging || !packaging.id) {
        return acc;
      }

      lossCategories.forEach((lossCategory) => {
        acc.push(`${packaging.id}-${lossCategory.id}`);
      });

      return acc;
    }, []);

    const updatedSupplierProductsDropdownItems = supplierProductsDropdownItems.map(
      (supplierProduct) => {
        if (loss.supplierProductId === supplierProduct.supplierProductId) {
          return {
            ...loss,
            // +1 to take current loss into account
            isDisabled: allPossibleKeysForSP.length === allAssociatedLossesCompositeKeys.length + 1,
          };
        }

        return supplierProduct;
      },
    );

    setSupplierProductsDropdownItems(updatedSupplierProductsDropdownItems);
  };

  const handleItemRemoval = (item) => {
    setIsDirty(true);

    const newSupplierProductRows = supplierProductRows.filter((row) => row.id !== item.id);

    setSupplierProductRows(newSupplierProductRows);

    const updatedSupplierProductsDropdownItems = supplierProductsDropdownItems.map(
      (supplierProduct) => {
        if (item.supplierProductId === supplierProduct.supplierProductId) {
          return {
            ...supplierProduct,
            isDisabled: false,
          };
        }

        return supplierProduct;
      },
    );

    setSupplierProductsDropdownItems(updatedSupplierProductsDropdownItems);
  };

  const handleFieldUpdate = (field, value, lossId) => {
    setIsDirty(true);

    const updatedSupplierProductRow = supplierProductRows.map((row) => {
      if (row.id === lossId) {
        row[field] = value;

        // When changing the packaging of a loss, we need to:
        // 1. Group all losses by supplier product ID to get associated losses
        // 2. Check if the combination of new packaging + current loss category already exists
        // 3. If it doesn't exist, we can use this combination as the new key
        // 4. If it exists, we need to:
        //    - Reset the loss category to undefined
        //    - Find a new valid loss category that isn't used with this packaging
        if (field === 'supplierProductPackagingId') {
          const supplierProductRowGroupedBySPids = groupBy(
            supplierProductRows,
            'supplierProductId',
          );
          const associatedLosses = supplierProductRowGroupedBySPids[row.supplierProductId] || [];

          // Get all existing composite keys in format "packagingId-lossCategoryId"
          const allAssociatedLossesCompositeKeys = associatedLosses.reduce((acc, { key }) => {
            if (!!key) {
              acc.push(key);
            }

            return acc;
          }, []);

          // Try to create a new key with the new packaging ID and current loss category
          const potentialKey = `${value}-${row.lossCategoryId}`;

          // If this combination doesn't exist yet, we can use it
          if (!allAssociatedLossesCompositeKeys.includes(potentialKey)) {
            row.key = potentialKey;
            return row;
          }

          // We set the loss category to undefined to keep the difference between no category and category none
          row.lossCategoryId = undefined;

          // Find a new valid loss category that isn't used with this packaging
          row = updateLossWithCategoryUnicity(row, allAssociatedLossesCompositeKeys);
        }

        if (field === 'lossCategoryId') {
          row.key = `${row.supplierProductPackagingId}-${value}`;
        }
      }

      return row;
    });

    setSupplierProductRows(updatedSupplierProductRow);
  };

  const handleRowSelectionChange = (selectedRowIds) => {
    const selectedRowIdsSet = new Set(selectedRowIds);

    const updatedSupplierProductRows = supplierProductRows.map((row) => ({
      ...row,
      isRowSelected: selectedRowIdsSet.has(row.id),
    }));

    setSupplierProductRows(updatedSupplierProductRows);
  };

  const handleReplacePicture = async (event, lossId) => {
    const file = event.target.files[0];

    if (file) {
      // We need to rename the file to avoid conflicts if multiple files with the same name are uploaded
      const newFileName = REGEX_IMAGE_FILE_EXTENSION.test(file.name)
        ? file.name.replace(REGEX_IMAGE_FILE_EXTENSION, `_${uuidv7()}.$1`)
        : `${file.name}_${uuidv7()}`;

      const renamedFile = new File([file], newFileName, { type: file.type });

      const loadedPicture = await utils.load(renamedFile);
      handleFieldUpdate('lossPictureUrl', null, lossId);
      handleFieldUpdate('lossPictureImage', loadedPicture, lossId);
    }
  };

  const handleDeletePicture = (lossId) => {
    handleFieldUpdate('lossPictureUrl', null, lossId);
    handleFieldUpdate('lossPictureImage', null, lossId);
  };

  useEffect(() => {
    setColumns(
      getSupplierProductsColumns({
        lossCategories,
        currency,
        handleFieldUpdate,
        handleItemRemoval,
        supplierProductRows,
        canCreateOrUpdateLosses,
        canCorrectLosses,
        handleReplacePicture,
        handleDeletePicture,
        setShouldPictureFullScreen,
      }),
    );

    const spTotal = supplierProductRows.reduce((acc, row) => {
      const priceForPackaging = row.priceByPackagingId[row.supplierProductPackagingId];

      return acc + priceForPackaging * row.quantity;
    }, 0);

    setSupplierProductsTotal(spTotal);
  }, [
    lossCategories,
    supplierProductRows,
    supplierProductsDropdownItems,
    canCorrectLosses,
    canCreateOrUpdateLosses,
  ]);

  return (
    <DataGridContainer>
      <DataGridHeader>
        <Dropdown
          iconSrc={'/images/inpulse/search-dmgrey.svg'}
          isDisabled={isLoading || !canCreateOrUpdateLosses || !canCorrectLosses}
          items={supplierProductsDropdownItems}
          placeholder={i18next.t('LOSSES.INVENTORIES.DROPDOWN_PLACEHOLDER')}
          searchPlaceholder={i18next.t('GENERAL.SEARCH')}
          selectedItem={null}
          width={INPUT_WIDTH.XXL}
          isRequired
          onSelectionChange={addNewLoss}
        />
        <DataGridHeaderTextContainer>
          <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('LOSSES.INVENTORIES.NUMBER_OF_LINES', {
              count: supplierProductRows.length,
            })}
          </Text>
          <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('LOSSES.INVENTORIES.TOTAL_EXCL_TAXES', {
              currencyCode: currency.alphabeticCode,
              total: formatNumber(supplierProductsTotal, currency.numberDecimals),
            })}
          </Text>
        </DataGridHeaderTextContainer>
      </DataGridHeader>
      <GridContainer>
        <DataGrid
          columns={columns}
          rows={supplierProductRows}
          slots={{
            noRowsOverlay: () => renderEmptyState(selectedDate, selectedStore),
          }}
          sx={{
            height: '100%',
            border: '0px',
            // disable cell selection style
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
              {
                outline: 'none',
              },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '14px',
            },
          }}
          autosizeOnMount
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooter
          ignoreDiacritics
          onRowSelectionModelChange={handleRowSelectionChange}
        />
      </GridContainer>
    </DataGridContainer>
  );
};

export default SupplierProductsGrid;
